import EventEmitter from 'events';

import ChatService from './chat';

// Shared source of truth for "do I have unread messages" so the nav can show a badge on
// any page, not just inside the chat screen. The chat page pushes authoritative counts as
// it marks things read / receives messages; everywhere else we refresh from the inbox API.
const emitter = new EventEmitter();
// Desktop + mobile nav both subscribe, and old instances briefly overlap new ones during
// route transitions, so lift the default cap of 10 to avoid spurious warnings.
emitter.setMaxListeners(50);
const CHANGE_EVENT = 'unreadCountChange';

let unreadCount = 0;
let inFlight = null;

function countUnread(data) {
    const messages = Array.isArray(data?.messages) ? data.messages
        : Array.isArray(data?.inbox) ? data.inbox
        : Array.isArray(data?.conversations) ? data.conversations
        : Array.isArray(data) ? data
        : [];
    // The inbox marks only received-and-unseen copies with status UNREAD; the user's own
    // sent copies come back READ, so this counts just genuinely unread incoming messages.
    return messages.reduce((total, m) => total + (m.status === 'UNREAD' ? 1 : 0), 0);
}

const UnreadMessagesService = {
    subscribe(callback) {
        emitter.addListener(CHANGE_EVENT, callback);
    },

    unsubscribe(callback) {
        emitter.removeListener(CHANGE_EVENT, callback);
    },

    getCount() {
        return unreadCount;
    },

    // Called by the chat page, which knows the real per-conversation counts.
    setCount(count) {
        const next = Math.max(0, count | 0);
        if (next === unreadCount) return;
        unreadCount = next;
        emitter.emit(CHANGE_EVENT, unreadCount);
    },

    // Pulls the current unread total from the inbox. Safe to call on every nav mount; a
    // request already in flight is shared rather than duplicated. Never throws.
    async refresh() {
        if (inFlight) return inFlight;
        inFlight = ChatService.listInbox()
            .then(response => {
                UnreadMessagesService.setCount(countUnread(response?.data));
            })
            .catch(() => { /* leave the last known count in place */ })
            .finally(() => { inFlight = null; });
        return inFlight;
    },
};

export default UnreadMessagesService;
